import { styled } from "@mui/system";
import { Button, Link, TextField } from "@mui/material";

/**
 * Fondo con imagen (jpeg) + overlay
 */
export const LoginBackground = styled("div", {
    shouldForwardProp: (prop) => prop !== "bg",
})(({ bg }) => ({
    height: "100vh",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#0B0F17",
    backgroundImage: `url(${bg})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
}));

export const BackgroundOverlay = styled("div")({
    position: "absolute",
    inset: 0,
    zIndex: 1,
    background:
        "radial-gradient(1200px 800px at 20% 20%, rgba(0,0,0,0.30), rgba(0,0,0,0.72))",
    backdropFilter: "saturate(120%)",
});

/**
 * ✅ Contenedor principal (más pequeño y minimalista)
 */
export const LoginContainer = styled("div")({
    width: "100%",
    maxWidth: 860,      // ⬅️ antes 980
    minHeight: 460,     // ⬅️ antes 520
    display: "flex",
    borderRadius: 16,   // ⬅️ un poco más “tight”
    overflow: "hidden",
    position: "relative",
    zIndex: 2,

    background: "rgba(255, 255, 255, 0.10)",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    boxShadow: "0 16px 50px rgba(0, 0, 0, 0.52)",
    backdropFilter: "blur(14px)",

    "@media (max-width: 900px)": {
        maxWidth: 520,
        minHeight: "auto",
        flexDirection: "column",
    },
});

/**
 * Panel izquierda (marca) - padding reducido
 */
export const LeftContainer = styled("div")({
    width: "42%",
    padding: 28, // ⬅️ antes 36
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 12,

    background:
        "linear-gradient(135deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04))",
    borderRight: "1px solid rgba(255, 255, 255, 0.12)",

    "@media (max-width: 900px)": {
        width: "100%",
        padding: 22,
        borderRight: "none",
        borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
    },
});

export const RightContainer = styled("div")({
    width: "58%",
    padding: 32, // ⬅️ antes 42

    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "center",

    "@media (max-width: 900px)": {
        width: "100%",
        padding: 22,
    },
});

export const Logo = styled("img")({
    width: "76%",
    maxWidth: 210,      // el logo de Oddis Maps es vertical: menos ancho
    maxHeight: 260,     // y con tope de alto para no estirar el panel
    height: "auto",
    objectFit: "contain",
    filter: "drop-shadow(0 10px 22px rgba(0,0,0,0.35))",
    userSelect: "none",
});

export const BrandHint = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 12,
    letterSpacing: 1.7,
    textTransform: "uppercase",
    opacity: 0.85,
    color: "rgba(255,255,255,0.85)",
});

/**
 * Header del formulario
 */
export const FormHeader = styled("div")({
    width: "100%",
    color: "rgba(255,255,255,0.92)",
});

/**
 * Inputs minimalistas (label fix incluido)
 */
export const StyledTextField = styled(TextField)({
    width: "100%",

    "& .MuiInputLabel-root": {
        color: "rgba(255,255,255,0.72)",
        zIndex: 2,
    },

    "& .MuiInputLabel-root.MuiInputLabel-shrink": {
        transform: "translate(14px, -8px) scale(0.75)",
        padding: "0 6px",
        borderRadius: 8,
        background: "rgba(11, 15, 23, 0.55)",
    },

    "& .MuiOutlinedInput-root": {
        color: "rgba(255,255,255,0.92)",
        background: "rgba(255,255,255,0.06)",
        borderRadius: 12,
        transition: "background 160ms ease, border-color 160ms ease",
    },

    "& .MuiOutlinedInput-input": {
        padding: "13px 14px", // ⬅️ un pelín más compacto
    },

    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(255,255,255,0.16)",
    },

    "& .MuiOutlinedInput-root:hover": {
        background: "rgba(255,255,255,0.08)",
    },

    "& .MuiOutlinedInput-root.Mui-focused": {
        background: "rgba(255,255,255,0.10)",
    },

    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(255,255,255,0.40)",
    },

    "& .MuiFormHelperText-root": {
        color: "rgba(255,255,255,0.60)",
    },

    "& input:-webkit-autofill": {
        WebkitTextFillColor: "rgba(255,255,255,0.92)",
        WebkitBoxShadow: "0 0 0px 1000px rgba(255,255,255,0.06) inset",
        transition: "background-color 9999s ease-in-out 0s",
        borderRadius: 12,
    },
});

/**
 * Botones
 */
export const PrimaryButton = styled(Button)({
    borderRadius: 12,
    padding: "11px 14px", // ⬅️ más minimal
    fontWeight: 900,
    letterSpacing: 0.2,
    textTransform: "none",
    boxShadow: "none",

    backgroundColor: "var(--primary-color, #F59E0B)",
    color: "#fff",

    "&:hover": {
        backgroundColor: "var(--primary-color-dark, #D97706)",
        boxShadow: "0 10px 26px rgba(0,0,0,0.35)",
        transform: "translateY(-1px)",
    },

    "&:active": {
        transform: "translateY(0px)",
    },

    "&.Mui-disabled": {
        opacity: 0.7,
        color: "rgba(255,255,255,0.85)",
    },
});

export const SecondaryButton = styled(Button)({
    borderRadius: 12,
    padding: "11px 14px", // ⬅️ más minimal
    fontWeight: 900,
    letterSpacing: 0.2,
    textTransform: "none",

    color: "rgba(255,255,255,0.92)",
    borderColor: "rgba(255,255,255,0.28)",
    background: "rgba(255,255,255,0.04)",

    "&:hover": {
        borderColor: "rgba(255,255,255,0.45)",
        background: "rgba(255,255,255,0.06)",
        transform: "translateY(-1px)",
    },

    "&:active": {
        transform: "translateY(0px)",
    },

    "&.Mui-disabled": {
        opacity: 0.7,
    },
});

/**
 * Link "Forgot password" como botón (minimal)
 */
export const StyledForgotPassword = styled(Link)({
    width: "fit-content",
    alignSelf: "center",
    marginTop: 8, // ⬅️ menos espacio

    color: "rgba(255,255,255,0.78)",
    fontSize: "0.92rem",
    textDecoration: "none",
    cursor: "pointer",
    border: "none",
    background: "transparent",
    padding: 6,

    transition: "opacity 160ms ease, transform 160ms ease",

    "&:hover": {
        opacity: 1,
        transform: "translateY(-1px)",
        color: "rgba(255,255,255,0.95)",
    },
});
